import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Lock, Save, Calendar as CalendarIcon, Download, Filter, User } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { initials } from "../lib/utils";

const DAYS = [
  { key: "lunes",     label: "Lunes",     idx: 0 },
  { key: "martes",    label: "Martes",    idx: 1 },
  { key: "miercoles", label: "Miércoles", idx: 2 },
  { key: "jueves",    label: "Jueves",    idx: 3 },
  { key: "viernes",   label: "Viernes",   idx: 4 },
  { key: "sabado",    label: "Sábado",    idx: 5 },
  { key: "domingo",   label: "Domingo",   idx: 6 },
];

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

function mondayOf(d) {
  const dd = new Date(d);
  const day = dd.getDay() || 7; // Sunday → 7
  dd.setDate(dd.getDate() - day + 1);
  dd.setHours(0,0,0,0);
  return dd;
}
function isoDate(d) {
  const dd = new Date(d);
  const y = dd.getFullYear();
  const m = String(dd.getMonth()+1).padStart(2,"0");
  const day = String(dd.getDate()).padStart(2,"0");
  return `${y}-${m}-${day}`;
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return isoDate(d);
}
function humanDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} de ${MONTHS_ES[d.getMonth()].toLowerCase()}, ${d.getFullYear()}`;
}

export default function WeeklyPlannerPage() {
  const { user } = useAuth();
  const isColab = user.role === "colaborador";
  const isReadOnly = user.role === "lectura";
  const isManager = user.role === "administrador" || user.role === "coordinador";

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Planificación</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Planificador Semanal</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isColab && "Registre las actividades programadas para cada día de la semana. Una vez cerrada la semana, la información quedará bloqueada."}
          {isManager && "Consulte y exporte las planificaciones semanales del equipo por semana, colaborador y área."}
          {isReadOnly && "Vista de sólo lectura de las planificaciones semanales del equipo."}
        </p>
      </div>

      {isColab ? <ColabView /> : <ManagerView userRole={user.role} userId={user.id} />}
    </div>
  );
}

function ColabView() {
  const [weekStart, setWeekStart] = useState(isoDate(mondayOf(new Date())));
  const [plan, setPlan] = useState(null);
  const [days, setDays] = useState({});
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async (ws) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/weekly-plans/mine?week_start=${ws}`);
      setPlan(data);
      setDays(data.days || {});
      setNotes(data.notes || "");
      setDirty(false);
    } catch (e) { toast.error("Error al cargar la planificación"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(weekStart); }, [weekStart]);

  const save = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/weekly-plans/${plan.id}`, { week_start: plan.week_start, days, notes });
      setPlan(data); setDirty(false);
      toast.success("Planificación guardada");
    } catch (e) {
      const status = e.response?.status;
      if (status === 423) toast.error("Semana cerrada. Ya no puede editarse.");
      else toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const shiftWeek = (n) => setWeekStart(ws => addDays(ws, n * 7));
  const goCurrent = () => setWeekStart(isoDate(mondayOf(new Date())));

  const locked = plan?.locked;
  const weekEnd = weekStart ? addDays(weekStart, 6) : "";

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-3 items-center">
        <CalendarIcon className="w-4 h-4 text-slate-500" />
        <button onClick={() => shiftWeek(-1)} className="p-2 border border-slate-300 rounded-md hover:bg-slate-50" data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></button>
        <div className="text-sm font-semibold text-slate-800 min-w-[240px] text-center">
          Semana del {humanDate(weekStart)} al {humanDate(weekEnd)}
        </div>
        <button onClick={() => shiftWeek(1)} className="p-2 border border-slate-300 rounded-md hover:bg-slate-50" data-testid="week-next"><ChevronRight className="w-4 h-4" /></button>
        <button onClick={goCurrent} className="text-xs px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50">Semana actual</button>
        <div className="ml-auto flex items-center gap-3">
          {locked ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 font-semibold">
              <Lock className="w-3 h-3" /> Semana cerrada
            </span>
          ) : (
            <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 font-semibold">Semana editable</span>
          )}
          {!locked && (
            <button
              onClick={save}
              disabled={saving || !dirty}
              data-testid="save-plan-btn"
              className="inline-flex items-center gap-2 bg-crejpp hover:bg-crejpp-dark disabled:opacity-50 text-white text-sm font-semibold rounded-md px-4 py-2"
            >
              <Save className="w-4 h-4" /> {saving ? "Guardando…" : dirty ? "Guardar cambios" : "Guardado"}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center text-sm text-slate-500 py-16">Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {DAYS.map((d) => {
              const date = addDays(weekStart, d.idx);
              return (
                <div key={d.key} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <div className="flex items-baseline justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                    <div className="font-heading font-bold text-slate-900 text-sm">{d.label}</div>
                    <div className="text-[11px] text-slate-500">{humanDate(date)}</div>
                  </div>
                  <textarea
                    rows={4}
                    disabled={locked}
                    value={days[d.key] || ""}
                    onChange={(e) => { setDays({...days, [d.key]: e.target.value}); setDirty(true); }}
                    placeholder={locked ? "" : "Actividades programadas para este día…"}
                    className="w-full p-3 text-sm resize-y focus:outline-none focus:bg-red-50/20 disabled:bg-slate-50 disabled:text-slate-500"
                    data-testid={`day-${d.key}`}
                  />
                </div>
              );
            })}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
              <div className="font-heading font-bold text-slate-900 text-sm">Notas adicionales</div>
            </div>
            <textarea
              rows={3}
              disabled={locked}
              value={notes}
              onChange={(e) => { setNotes(e.target.value); setDirty(true); }}
              placeholder={locked ? "" : "Observaciones, cambios de planes, coordinaciones necesarias…"}
              className="w-full p-3 text-sm resize-y focus:outline-none focus:bg-red-50/20 disabled:bg-slate-50 disabled:text-slate-500"
              data-testid="plan-notes"
            />
          </div>

          {locked && (
            <div className="text-xs text-slate-500 text-center py-2">
              Esta semana ya cerró — sólo un administrador puede realizar ajustes.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ManagerView({ userRole, userId }) {
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [plans, setPlans] = useState([]);
  const [filters, setFilters] = useState({ week_start: "", user_id: "", area_id: "" });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [u, a, w] = await Promise.all([api.get("/users"), api.get("/areas"), api.get("/weekly-plans/weeks")]);
      setUsers(u.data); setAreas(a.data); setWeeks(w.data);
      const currentWeek = w.data[0] || isoDate(mondayOf(new Date()));
      setFilters(f => ({ ...f, week_start: currentWeek }));
    })();
  }, []);

  useEffect(() => {
    if (!filters.week_start && !filters.user_id && !filters.area_id) return;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k,v]) => v && params.append(k, v));
        const { data } = await api.get(`/weekly-plans?${params.toString()}`);
        setPlans(data);
      } catch { toast.error("Error al cargar"); }
      finally { setLoading(false); }
    };
    load();
  }, [filters]);

  const areaMap = Object.fromEntries(areas.map(a => [a.id, a.name]));
  const userMap = Object.fromEntries(users.map(u => [u.id, u]));

  const dl = async (fmt) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k,v]) => v && params.append(k, v));
      const token = localStorage.getItem("crejpp_token");
      const res = await fetch(`${API}/weekly-plans/reports/${fmt}?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fmt === "pdf" ? "planificacion_semanal.pdf" : "planificacion_semanal.xlsx";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Reporte generado");
    } catch { toast.error("No se pudo generar el reporte"); }
  };

  return (
    <div className="space-y-4">
      {userRole === "coordinador" && users.filter(u => u.role === "colaborador").length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg px-4 py-3" data-testid="no-collabs-banner">
          No tiene colaboradores asignados. Comuníquese con el Administrador.
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-slate-500" />
        <select value={filters.week_start} onChange={e => setFilters({...filters, week_start: e.target.value})} className="text-sm px-3 py-2 border border-slate-300 rounded-md" data-testid="filter-week">
          <option value="">Todas las semanas</option>
          {weeks.map(w => <option key={w} value={w}>Semana del {humanDate(w)}</option>)}
        </select>
        <select value={filters.user_id} onChange={e => setFilters({...filters, user_id: e.target.value})} className="text-sm px-3 py-2 border border-slate-300 rounded-md" data-testid="filter-user">
          <option value="">Todos los colaboradores</option>
          {users.filter(u => (u.role === "colaborador" || u.role === "coordinador") && u.id !== userId).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select value={filters.area_id} onChange={e => setFilters({...filters, area_id: e.target.value})} className="text-sm px-3 py-2 border border-slate-300 rounded-md" data-testid="filter-area-plans">
          <option value="">Todas las áreas</option>
          {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <div className="ml-auto flex gap-2">
          <button onClick={() => dl("pdf")} className="inline-flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-2 text-sm hover:bg-slate-50" data-testid="dl-plan-pdf"><Download className="w-4 h-4" /> PDF</button>
          <button onClick={() => dl("excel")} className="inline-flex items-center gap-1.5 bg-crejpp hover:bg-crejpp-dark text-white rounded-md px-3 py-2 text-sm font-semibold" data-testid="dl-plan-excel"><Download className="w-4 h-4" /> Excel</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold px-4 py-3 border-b border-slate-200 bg-slate-50">
            {loading ? "Cargando…" : `${plans.length} planificación(es)`}
          </div>
          <div className="max-h-[560px] overflow-y-auto divide-y divide-slate-100">
            {plans.map(p => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                data-testid={`plan-item-${p.id}`}
                className={`w-full text-left p-4 hover:bg-slate-50 flex items-start gap-3 ${selected?.id === p.id ? "bg-red-50/40 border-l-2 border-crejpp" : ""}`}
              >
                <div className="w-9 h-9 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center shrink-0">{initials(p.user_name)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 text-sm truncate">{p.user_name}</div>
                  <div className="text-xs text-slate-500">{areaMap[p.area_id] || "Sin área"}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Semana del {humanDate(p.week_start)}</div>
                </div>
                {p.locked && <Lock className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
              </button>
            ))}
            {!loading && plans.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-400" data-testid="plans-empty">
                {userRole === "coordinador" && !filters.user_id && !filters.area_id
                  ? "No tiene colaboradores asignados. Comuníquese con el Administrador."
                  : "Sin planificaciones para los filtros seleccionados."}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <PlanDetail plan={selected} areaMap={areaMap} />
          ) : (
            <div className="bg-white border border-dashed border-slate-200 rounded-lg p-12 text-center text-sm text-slate-400 flex flex-col items-center gap-2">
              <User className="w-8 h-8 text-slate-300" />
              Seleccione una planificación de la lista para ver el detalle.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanDetail({ plan, areaMap }) {
  const days = plan.days || {};
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Semana del {humanDate(plan.week_start)}</div>
          <h3 className="font-heading text-lg font-bold text-slate-900">{plan.user_name}</h3>
          <div className="text-xs text-slate-500">{areaMap[plan.area_id] || "Sin área"}</div>
        </div>
        {plan.locked ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 font-semibold">
            <Lock className="w-3 h-3" /> Cerrada
          </span>
        ) : (
          <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 font-semibold">En curso</span>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {DAYS.map(d => (
          <div key={d.key} className="px-5 py-3 flex gap-4">
            <div className="w-24 shrink-0">
              <div className="font-heading text-sm font-bold text-slate-900">{d.label}</div>
              <div className="text-[11px] text-slate-500">{humanDate(addDays(plan.week_start, d.idx))}</div>
            </div>
            <div className="flex-1 text-sm text-slate-700 whitespace-pre-wrap">{days[d.key] || <span className="text-slate-400 italic">Sin actividades registradas</span>}</div>
          </div>
        ))}
        {plan.notes && (
          <div className="px-5 py-3 flex gap-4 bg-slate-50">
            <div className="w-24 shrink-0"><div className="font-heading text-sm font-bold text-slate-900">Notas</div></div>
            <div className="flex-1 text-sm text-slate-700 whitespace-pre-wrap">{plan.notes}</div>
          </div>
        )}
      </div>
      <div className="px-5 py-2 border-t border-slate-100 text-[11px] text-slate-400">Última actualización: {plan.updated_at?.slice(0,19).replace("T"," ") || "—"} UTC</div>
    </div>
  );
}
